import { useQueries, useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';

import getCategoryBySlug from '../../components/pages/v2/vehicle/utils/get-category-by-slug';
import BrandsPage from '../../components/pages/vehicle/brands';
import useBreadcrumbs from '../../components/pages/vehicle/hooks/use-breadcrumbs';
import { useAppContext } from '../../components/providers/app-context';
import {
  ADVANCED_VIEW_ACTIVE,
  DETAILED_PRICES,
  SHIPPING_LOCATION,
} from '../../static/cookies';
import forceShippingUKForCoUK from '../../utils/force-shipping-uk-for-co-uk';
import { isGlobalUser } from '../../utils/is-global-user';
import { isUKuser } from '../../utils/is-uk-user';
import { isUSuser } from '../../utils/is-us-user';
import getBrands from '../../utils/react-query/query-options/get-brands';
import getGroups from '../../utils/react-query/query-options/get-groups';
import getLasEntries from '../../utils/react-query/query-options/get-last-entries';
import getLoggedUser from '../../utils/react-query/query-options/get-logged-user';
import getStock from '../../utils/react-query/query-options/get-stocks';
import getTranslatedSlugs from '../../utils/react-query/query-options/get-translated-slugs';
import { redirectForAdvanced } from '../../utils/redirect-for-advanced';
import slugChange301 from '../../utils/slug-change-301';
import validateCategoryPartSlugs from '../../utils/validate-category-part-slugs';
import getLastVehicles from '../../utils/react-query/query-options/get-last-vehicles';

const useData = () => {
  const {
    locale = 'en',
    query: { category = '', subCategory = '' },
  } = useRouter();
  const { detailedPrice } = useAppContext();
  const isUK = process.env.NEXT_PUBLIC_HOST?.includes('.co.uk');
  const isEnglish = isUK || locale === 'en';

  const hasSelection = !!(category && subCategory);
  const selection = hasSelection
    ? {
      category: { category, subCategory },
    }
    : undefined;

  const queries = {
    queries: [
      getGroups({ locale }),
      getBrands({ locale }),
      getLasEntries({
        locale,
        part: subCategory as string || '',
        vehicle: '',
        detailed_prices: !!detailedPrice,
        ...(isEnglish && { page: category ? undefined : 'brands', category: category as string || '', })
      }),
      isEnglish ? getLastVehicles({
        locale,
        part: subCategory as string || '',
        vehicle: '',
        ...(isEnglish && { page: category ? undefined : 'brands', category: category as string || '', })
      }) : [],
    ].map(([key, fn]) => ({
      queryKey: key,
      queryFn: fn,
      keepPreviousData: true,
    })),
  };

  const [
    { data: groups },
    { data: brands },
    { data: lastEntries, isFetched: isFetchedLastEntries },
    { data: lastVehicles },
  ] = useQueries(queries);

  let vehiclesLastEntries = lastVehicles;
  let productsLastEntries = lastEntries;

  if (!isEnglish) {
    vehiclesLastEntries = lastVehicles && lastVehicles?.slice(0, 8);
    productsLastEntries = lastEntries && lastEntries?.slice(0, 12);
  }

  // GTM
  useEffect(() => {
    if (isFetchedLastEntries && productsLastEntries && productsLastEntries.length > 0) {
      // @ts-ignore
      window.dataLayer.push({
        event: 'eec.impressionView',
        ecommerce: {
          currencyCode: 'EUR',
          impressions: productsLastEntries?.map(
            (item: TProductListItem, index: number) => ({
              list: 'Last Parts in Stock', // List name where the impression was viewed
              id: item.sku, // SKU of the product
              name: item.part_en, // name of the product.
              brand: item.car_brand, // car brand of the part
              category: item.category_en_old, // Product category(ies) - each "/" is a category level (max 5 levels)
              price: item.pricing?.price_product
                ? Number(item.pricing.price_product).toFixed(2)
                : null, // price of the part without shipping, tax and currency
              position: index, // the position of the item in the list
              dimension2: item.reference, // reference of the product
              dimension3: item.car_model, // car model of the part
              dimension4: item.car_version, // car version of the part
            })
          ),
        },
      });

      // GA4
      window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      window.dataLayer.push({
        event: 'view_item_list',
        ecommerce: {
          currency: 'EUR', // Currency of the items associated with the event, in 3-letter ISO 4217 format
          items: productsLastEntries?.map((item: TProductListItem, index: number) => ({
            item_list_name: 'Last Parts in Stock', // List name where the impression was viewed
            item_list_id: 'last-parts-in-stock', // List name where the impression was viewed
            item_id: item.sku, // SKU of the product
            item_name: item.part_en, // name of the product.
            item_brand: item.car_brand, // car brand of the part
            item_category: item.category_en, // Product category(ies) - each "/" is a category level (max 5 levels)
            item_category2: item.part_en, // part name === product name
            price: item.pricing?.price_product
              ? Number(item.pricing.price_product).toFixed(2)
              : null, // price of the part without shipping, tax and currency
            index: index, // the position of the item in the list
            quantity: '1', // Hardcoded to 1
          })),
        },
      });
    }
  }, [productsLastEntries, isFetchedLastEntries]);

  const breadcrumbs = useBreadcrumbs({
    selection,
    groups,
  });

  const partsInfo = useMemo(
    () => getCategoryBySlug({ category, subCategory }, groups),
    [category, subCategory, groups]
  );

  const { data: stock } = useQuery(
    ...getStock({
      locale,
      stocks: [
        {
          part: subCategory as string,
        },
      ],
    }),
    {
      enabled: !!subCategory,
      keepPreviousData: true,
    }
  );

  const { data: translatedSlugs } = useQuery(
    ...getTranslatedSlugs({
      lang_origin: locale,
      lang_trans: null,
      slugs: [
        {
          slug_origin: category as string,
          slug_type: 'category',
        },
        {
          slug_origin: subCategory as string,
          slug_type: 'part',
        },
      ],
    }),
    {
      enabled: !!subCategory && !!category,
      keepPreviousData: true,
    }
  );

  return {
    groups,
    brands,
    breadcrumbs,
    stock,
    partsInfo,
    hasSelection: hasSelection,
    selection,
    lastEntries: productsLastEntries,
    lastVehicles: vehiclesLastEntries,
    translatedSlugs,
  };
};

export default function Brands() {
  const data = useData();

  return (
    <main>
      <BrandsPage {...data} />
    </main>
  );
}


export const getServerSideProps: GetServerSideProps = async ({
  req,
  res,
  locale = 'en',
  query,
  params,
  resolvedUrl,
}) => {
  if (!!process.env.NEXT_PUBLIC_BRAND_SLUG) {
    return { notFound: true };
  }
  isUSuser(req, res);
  isUKuser(req, res);
  isGlobalUser(req, res);
  await forceShippingUKForCoUK({ req, res, locale });
  const isUK = process.env.NEXT_PUBLIC_HOST?.includes('.co.uk');
  const isEnglish = isUK || locale === 'en';

  const { category = '', subCategory = '' } = query;
  const { default: getGenericClient } = await import('../../server/client');
  const genericClient = getGenericClient();

  try {
    const [user, groups] = await Promise.all([
      genericClient.fetchQuery(...getLoggedUser({ locale }, req)),
      genericClient.fetchQuery(...getGroups({ locale }, req)),
      category &&
      subCategory &&
      genericClient.prefetchQuery(
        ...getTranslatedSlugs(
          {
            lang_origin: locale,
            lang_trans: null,
            slugs: [
              {
                slug_origin: category as string,
                slug_type: 'category',
              },
              {
                slug_origin: subCategory as string,
                slug_type: 'part',
              },
            ],
          },
          req
        )
      ),
      genericClient.prefetchQuery(...getBrands({ locale }, req)),
      subCategory &&
      genericClient.prefetchQuery(
        ...getStock(
          {
            locale,
            stocks: [
              {
                part: subCategory as string,
              },
            ],
          },
          req
        )
      ),
      genericClient.prefetchQuery(
        ...getLasEntries({
          locale,
          vehicle: '',
          part: subCategory as string || '',
          detailed_prices: !!req.cookies[DETAILED_PRICES],
          ...(isEnglish && { page: category ? undefined : 'brands', category: category as string || '', })
        }, req)
      ),
      isEnglish ? genericClient.prefetchQuery(
        ...getLastVehicles({
          locale,
          vehicle: '',
          part: subCategory as string || '',
          ...(isEnglish && { page: category ? undefined : 'brands', category: category as string || '', })
        }, req)
      ) : null,
    ]);

    await validateCategoryPartSlugs({
      locale,
      groups,
      category: category as string,
      subCategory: subCategory as string,
      req,
    });

    const { default: retrieveStateAndClear } = await import(
      '../../utils/retrieve-state-and-clear'
    );

    if (
      user?.staff?.is_pro &&
      !!parseInt(req.cookies[ADVANCED_VIEW_ACTIVE] || '0')
    ) {
      return redirectForAdvanced({
        locale,
        category: category as string,
        subCategory: subCategory as string,
      });
    }

    return {
      props: {
        dehydratedState: retrieveStateAndClear(genericClient),
        shippingLocation: parseInt(req.cookies[SHIPPING_LOCATION], 10),
        detailPrices: req.cookies[DETAILED_PRICES] || null,
      },
    };
  } catch (e) {
    if (e.status === 301) {
      const newQuery: { [key: string]: string } = {
        ...params,
        ...(e.data.category && { category: e.data.category }),
        ...(e.data.part && { subCategory: e.data.part }),
      };

      return slugChange301({
        locale,
        query: newQuery,
        res,
        page: params?.category ? 'brands-with-part' : 'brands',
      });
    } else if (e.status < 500) {
      if (
        e?.data?.detail === 'part_identifier' ||
        e?.data?.detail === 'group_identifier'
      ) {
        return slugChange301({
          locale,
          query: {},
          res,
          page: 'brands',
          status: 307,
        });
      }

      return { notFound: true };
    }
    console.log(e);
    throw e;
  }
};
